import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, ArrowRight, ArrowUpRight, MessageCircle } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SERVICES } from "@/data/services";
import { INDUSTRIES } from "@/data/industries";
import { waLink } from "@/data/site";

const SERVICE_ITEMS = SERVICES.map((s) => ({ name: s.name, to: `/services/${s.slug}` }));
const INDUSTRY_ITEMS = INDUSTRIES.map((i) => ({ name: i.name, to: `/industries/${i.slug}` }));

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", items: SERVICE_ITEMS },
  { label: "Industries", to: "/industries", items: INDUSTRY_ITEMS },
  { label: "Solutions", to: "/solutions" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const linkCls = ({ isActive }) =>
  `relative px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "text-ink" : "text-ink/70 hover:text-ink"}`;

const Dropdown = ({ item }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const btn = useRef(null);
  const { pathname } = useLocation();
  const id = `menu-${item.label.toLowerCase()}`;

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); btn.current?.focus(); } };
    const onDown = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [open]);

  return (
    <div
      ref={wrap}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={(e) => { if (!wrap.current?.contains(e.relatedTarget)) setOpen(false); }}
    >
      <button
        ref={btn}
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-semibold ${pathname.startsWith(item.to) ? "text-ink" : "text-ink/70 hover:text-ink"}`}
        data-testid={`nav-${item.label.toLowerCase()}-dropdown`}
      >
        {item.label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      <div id={id} className={`absolute left-1/2 top-full -translate-x-1/2 pt-3 transition-[opacity,transform] duration-300 ${open ? "visible translate-y-0 opacity-100" : "invisible -translate-y-2 opacity-0"}`}>
        <div className="on-dark w-[680px] rounded-xl bg-ink p-6 shadow-2xl">
          <ul className="grid grid-cols-2 gap-x-6 gap-y-1">
            {item.items.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="group flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium text-white/85 hover:bg-white/5 hover:text-lime" data-testid={`dropdown-link-${l.to.split("/").pop()}`}>
                  {l.name}
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
          <Link to={item.to} className="mt-4 flex items-center gap-2 border-t border-white/10 px-3 pt-4 text-sm font-bold text-lime" data-testid={`dropdown-all-${item.label.toLowerCase()}`}>
            View all {item.label.toLowerCase()} <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </div>
  );
};

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" aria-label="Open menu" className="grid h-11 w-11 place-items-center rounded-full border border-ink/15 lg:hidden" data-testid="mobile-menu-button">
          <Menu className="h-5 w-5" aria-hidden />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full overflow-y-auto bg-paper p-6 sm:max-w-md" data-testid="mobile-menu">
        <SheetTitle className="sr-only">Site navigation</SheetTitle>
        <nav className="mt-10 flex flex-col" aria-label="Mobile">
          {NAV.map((n) =>
            n.items ? (
              <details key={n.label} className="group border-b border-ink/10">
                <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between font-display text-2xl font-semibold text-ink" data-testid={`mobile-${n.label.toLowerCase()}-toggle`}>
                  {n.label}
                  <ChevronDown className="h-5 w-5 transition-transform group-open:rotate-180" aria-hidden />
                </summary>
                <ul className="pb-4">
                  <li><Link to={n.to} className="block py-2.5 text-sm font-bold text-sageDeep">All {n.label.toLowerCase()}</Link></li>
                  {n.items.map((l) => (
                    <li key={l.to}><Link to={l.to} className="block py-2.5 text-sm font-medium text-ink/80">{l.name}</Link></li>
                  ))}
                </ul>
              </details>
            ) : (
              <Link key={n.label} to={n.to} className="flex min-h-[56px] items-center border-b border-ink/10 font-display text-2xl font-semibold text-ink" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                {n.label}
              </Link>
            )
          )}
          <Link to="/process" className="flex min-h-[56px] items-center border-b border-ink/10 font-display text-2xl font-semibold text-ink">Our Process</Link>
        </nav>
        <div className="mt-8 flex flex-col gap-3">
          <Link to="/contact#inquiry" className="btn-lime" data-testid="mobile-menu-cta">Grow Your Business <ArrowRight className="h-4 w-4" aria-hidden /></Link>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-ghost"><MessageCircle className="h-4 w-4" aria-hidden /> Chat on WhatsApp</a>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${scrolled ? "border-b border-ink/10 bg-paper/90 backdrop-blur-xl" : "border-b border-transparent bg-paper/60 backdrop-blur-sm"}`} data-testid="site-header">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-ink focus:px-4 focus:py-2 focus:text-white">Skip to content</a>
      <div className="container-x flex h-[72px] items-center justify-between gap-4">
        <Link to="/" className="shrink-0" aria-label="Athlevo IT Solution — Home" data-testid="header-logo">
          <img src="/images/athlevo-logo-header.webp" alt="Athlevo IT Solution" width="520" height="150" className="h-9 w-auto md:h-11" />
        </Link>
        <nav className="hidden items-center lg:flex" aria-label="Main">
          {NAV.map((n) =>
            n.items ? <Dropdown key={n.label} item={n} /> : (
              <NavLink key={n.label} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>{n.label}</NavLink>
            )
          )}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/contact#inquiry" className="btn-lime hidden !py-3 sm:inline-flex" data-testid="header-cta-button">
            Grow Your Business <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
          <MobileMenu />
        </div>
      </div>
    </header>
  );
};
